import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Paper, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
  Alert,
  MenuItem
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { User, UserRole, UserRegistration } from '../types/user';
import { getAllUsers, updateUser, deleteUser, registerUser, getCurrentUser } from '../services/userService';
import { counties, subCounties, facilities, County, SubCounty, Facility } from '../data/locations';
import { Navigate } from 'react-router-dom';
import TopBar from '../components/TopBar';

interface UserManagementProps {
  onLogout: () => void;
}

const UserManagement = ({ onLogout }: UserManagementProps): React.JSX.Element => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(getCurrentUser());
  
  // Dialog states
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [openAddDialog, setOpenAddDialog] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<Partial<User>>({});
  const [addUserData, setAddUserData] = useState<UserRegistration>({
    name: '',
    email: '',
    password: '',
    role: UserRole.FACILITY_USER,
    facility: '',
    county: '',
    subCounty: ''
  });
  const [dialogLoading, setDialogLoading] = useState<boolean>(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  
  // Fetch all users on component mount
  useEffect(() => {
    fetchUsers();
  }, []);
  
  // Redirect non-admin users to dashboard
  if (!currentUser || currentUser.role !== UserRole.CQI_MANAGER) {
    return <Navigate to="/dashboard" replace />;
  }
  
  // Filter subcounties based on selected county
  const filteredSubCounties = formData.county 
    ? subCounties.filter((sc: SubCounty) => sc.countyName === formData.county)
    : [];
    
  // Filter facilities based on selected subcounty
  const filteredFacilities = formData.subCounty
    ? facilities.filter((f: Facility) => f.subCountyName === formData.subCounty)
    : [];
  
  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const fetchedUsers = await getAllUsers();
      setUsers(fetchedUsers);
    } catch (err: any) {
      console.error('Failed to fetch users:', err);
      setError(err.response?.data?.message || 'Failed to fetch users. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleOpenEditDialog = (user: User) => {
    setSelectedUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      role: user.role,
      facility: user.facility,
      county: user.county,
      subCounty: user.subCounty
    });
    setOpenEditDialog(true);
  };
  
  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSelectedUser(null);
    setFormData({});
    setDialogError(null);
  };
  
  const handleOpenDeleteDialog = (user: User) => {
    setSelectedUser(user);
    setOpenDeleteDialog(true);
  };
  
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedUser(null);
    setDialogError(null);
  };

  const handleOpenAddDialog = () => {
    setAddUserData({
      name: '',
      email: '',
      password: '',
      role: UserRole.FACILITY_USER,
      facility: '',
      county: '',
      subCounty: ''
    });
    setOpenAddDialog(true);
  };
  
  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setDialogError(null);
  };
  
  const handleAddInputChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    
    // Reset dependent fields when parent field changes
    if (name === 'county') {
      setAddUserData(prev => ({
        ...prev,
        county: value as string,
        subCounty: '',
        facility: ''
      }));
    } else if (name === 'subCounty') {
      setAddUserData(prev => ({
        ...prev,
        subCounty: value as string,
        facility: ''
      }));
    } else if (name === 'role') {
      // Reset location fields when role changes
      const newRole = value as UserRole;
      if (newRole === UserRole.NATIONAL_USER || newRole === UserRole.CQI_MANAGER) {
        setAddUserData(prev => ({
          ...prev,
          role: newRole,
          county: '',
          subCounty: '',
          facility: ''
        }));
      } else {
        setAddUserData(prev => ({
          ...prev,
          role: newRole
        }));
      }
    } else {
      setAddUserData(prev => ({
        ...prev,
        [name as string]: value
      }));
    }
  };
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Reset dependent fields when parent field changes
    if (name === 'county') {
      setFormData({ ...formData, county: value, subCounty: '', facility: '' });
    } else if (name === 'subCounty') {
      setFormData({ ...formData, subCounty: value, facility: '' });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };
  
  const handleRoleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      role: e.target.value as UserRole
    });
  };
  
  const handleSubmitEdit = async () => {
    if (!selectedUser) return;
    
    setDialogLoading(true);
    setDialogError(null);
    
    try {
      const updatedUser = await updateUser(selectedUser._id, formData);
      
      // Update users list
      setUsers(users.map(user => 
        user._id === selectedUser._id ? updatedUser : user
      ));
      
      handleCloseEditDialog();
    } catch (err: any) {
      console.error('Failed to update user:', err);
      setDialogError(err.response?.data?.message || 'Failed to update user. Please try again.');
    } finally {
      setDialogLoading(false);
    }
  };
  
  const handleSubmitDelete = async () => {
    if (!selectedUser) return;
    
    setDialogLoading(true);
    setDialogError(null);
    
    
    try {
      await deleteUser(selectedUser._id);
      setUsers(prevUsers => prevUsers.filter(user => user._id !== selectedUser._id));
      handleCloseDeleteDialog();
    } catch (err: any) {
      console.error('Failed to delete user:', err);
      setDialogError(err.response?.data?.message || 'Failed to delete user. Please try again.');
    } finally {
      setDialogLoading(false);
    }
  };
  
  const handleSubmitAddUser = async () => {
    setDialogLoading(true);
    setDialogError(null);
    
    // Debug logging for subcounty user creation
    if (addUserData.role === UserRole.SUBCOUNTY_USER) {
     
      
      // Additional validation for subcounty users
      if (!addUserData.county || !addUserData.subCounty) {
        setDialogError('Subcounty users require both County and SubCounty to be specified');
        setDialogLoading(false);
        return;
      }
    }
    
    // Check for duplicate email before submission
    const duplicateEmail = users.some(user => user.email === addUserData.email);
    if (duplicateEmail) {
      setDialogError('Email is already in use. Please use a different email address.');
      setDialogLoading(false);
      return;
    }
    
    try {
      // Use preserveSession=true to prevent overwriting the admin's session
      const newUser = await registerUser(addUserData, true);
      setUsers([...users, newUser.user]);
      handleCloseAddDialog();
    } catch (err: any) {
      console.error('Failed to add user:', err);
      setDialogError(err.response?.data?.message || 'Failed to add user. Please try again.');
    } finally {
      setDialogLoading(false);
    }
  };
  
  return (
    <>
      <Box sx={{ flexGrow: 1 }}>
        <TopBar onLogout={onLogout} />
      </Box>

    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate(-1)} color="primary">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            User Management
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={handleOpenAddDialog}
          startIcon={<FolderSharedIcon />}
        >
          Add User
        </Button>
      </Box>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      
      <Paper elevation={3} sx={{ mt: 3 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>CHU/WIT</TableCell>
                <TableCell>Sub-County</TableCell>
                <TableCell>County</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No users found.
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user._id}>
                    <TableCell>{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      {user.role === UserRole.CQI_MANAGER ? 'CQI Manager' : 
                       user.role === UserRole.NATIONAL_USER ? 'National User' : 
                       user.role === UserRole.COUNTY_USER ? 'County User' : 
                       user.role === UserRole.SUBCOUNTY_USER ? 'Sub-County User' : 
                       'CHU/WIT User'}
                    </TableCell>
                    <TableCell>{user.facility || '-'}</TableCell>
                    <TableCell>{user.subCounty || '-'}</TableCell>
                    <TableCell>{user.county || '-'}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleOpenEditDialog(user)} color="primary" size="small">
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleOpenDeleteDialog(user)} color="error" size="small">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      {/* Edit User Dialog */}
      <Dialog open={openEditDialog} onClose={handleCloseEditDialog} maxWidth="md" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          {dialogError && <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert>}
          
          <Box component="form" sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Name"
              name="name"
              value={formData.name || ''}
              onChange={handleChange}
              margin="normal"
            />
            
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email || ''}
              onChange={handleChange}
              margin="normal"
            />
            
            <FormControl component="fieldset" sx={{ mt: 2, display: 'block' }}>
              <FormLabel component="legend">Role</FormLabel>
              <RadioGroup
                value={formData.role || ''}
                onChange={handleRoleChange}
                sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
              >
                <FormControlLabel
                  value={UserRole.CQI_MANAGER}
                  control={<Radio />}
                  label="CQI Manager (Admin)"
                />
                <FormControlLabel
                  value={UserRole.NATIONAL_USER}
                  control={<Radio />}
                  label="National User (All Counties)"
                />
                <FormControlLabel
                  value={UserRole.COUNTY_USER}
                  control={<Radio />}
                  label="County User (Specific County)"
                />
                <FormControlLabel
                  value={UserRole.SUBCOUNTY_USER}
                  control={<Radio />}
                  label="Sub-County User (Specific Sub-County)"
                />
                <FormControlLabel
                  value={UserRole.FACILITY_USER}
                  control={<Radio />}
                  label="CHU/WIT User (Specific Facility)"
                />
              </RadioGroup>
            </FormControl>
            
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Only show county field for County and Facility users */}
              {(formData.role === UserRole.COUNTY_USER || formData.role === UserRole.FACILITY_USER || formData.role === UserRole.SUBCOUNTY_USER) && (
                <TextField
                  select
                  fullWidth
                  label="County"
                  name="county"
                  value={formData.county || ''}
                  onChange={handleChange}
                  required={formData.role === UserRole.COUNTY_USER || formData.role === UserRole.SUBCOUNTY_USER || formData.role === UserRole.FACILITY_USER}
                >
                  <MenuItem value="">
                    <em>Select County</em>
                  </MenuItem>
                  {counties.map((county: County) => (
                    <MenuItem key={county.name} value={county.name}>
                      {county.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              
              {/* Only show sub-county field for Facility users */}
              {(formData.role === UserRole.FACILITY_USER || formData.role === UserRole.SUBCOUNTY_USER) && (
                <TextField
                  select
                  fullWidth
                  label="Sub-County"
                  name="subCounty"
                  value={formData.subCounty || ''}
                  onChange={handleChange}
                  disabled={!formData.county}
                  required={formData.role === UserRole.FACILITY_USER || formData.role === UserRole.SUBCOUNTY_USER}
                >
                  <MenuItem value="">
                    <em>Select Sub-County</em>
                  </MenuItem>
                  {filteredSubCounties.map((subCounty: SubCounty) => (
                    <MenuItem key={subCounty.name} value={subCounty.name}>
                      {subCounty.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            
              
              {/* Only show facility field for Facility users */}
              {formData.role === UserRole.FACILITY_USER && (
                <TextField
                  select
                  fullWidth
                  label="Facility"
                  name="facility"
                  value={formData.facility || ''}
                  onChange={handleChange}
                  disabled={!formData.subCounty}
                  required={formData.role === UserRole.FACILITY_USER}
                >
                  <MenuItem value="">
                    <em>Select Facility</em>
                  </MenuItem>
                  {filteredFacilities.map((facility: Facility) => (
                    <MenuItem key={facility.name} value={facility.name}>
                      {facility.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog} disabled={dialogLoading}>Cancel</Button>
          <Button 
            onClick={handleSubmitEdit} 
            variant="contained" 
            color="primary"
            disabled={dialogLoading}
          >
            {dialogLoading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          {dialogError && <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert>}
          <Typography>
            Are you sure you want to delete user: {selectedUser?.name}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={dialogLoading}>Cancel</Button>
          <Button 
            onClick={handleSubmitDelete} 
            color="error" 
            variant="contained"
            disabled={dialogLoading}
          >
            {dialogLoading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog open={openAddDialog} onClose={handleCloseAddDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add New User</DialogTitle>
        <DialogContent>
          {dialogError && <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert>}
          <Box sx={{ mt: 2 }}>
            <TextField
              margin="dense"
              name="name"
              label="Full Name"
              type="text"
              fullWidth
              value={addUserData.name}
              onChange={handleAddInputChange}
              required
            />
            <TextField
              margin="dense"
              name="email"
              label="Email Address"
              type="email"
              fullWidth
              value={addUserData.email}
              onChange={handleAddInputChange}
              required
            />
            <TextField
              margin="dense"
              name="password"
              label="Password"
              type="password"
              fullWidth
              value={addUserData.password}
              onChange={handleAddInputChange}
              required
            />
            <FormControl component="fieldset" sx={{ mt: 2, mb: 1 }}>
              <FormLabel component="legend">User Role</FormLabel>
              <RadioGroup
                name="role"
                value={addUserData.role}
                onChange={handleAddInputChange}
                sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
              >
                <FormControlLabel value={UserRole.CQI_MANAGER} control={<Radio />} label="CQI Manager (Admin)" />
                <FormControlLabel value={UserRole.NATIONAL_USER} control={<Radio />} label="National User (All Counties)" />
                <FormControlLabel value={UserRole.COUNTY_USER} control={<Radio />} label="County User (Specific County)" />
                <FormControlLabel value={UserRole.SUBCOUNTY_USER} control={<Radio />} label="Sub-County User (Specific Sub-County)" />
                <FormControlLabel value={UserRole.FACILITY_USER} control={<Radio />} label="Facility User (Specific Facility)" />
              </RadioGroup>
            </FormControl>
            
            {/* Only show county field for County and Facility users */}
            {(addUserData.role === UserRole.COUNTY_USER || addUserData.role === UserRole.SUBCOUNTY_USER || addUserData.role === UserRole.FACILITY_USER) && (
              <TextField
                select
                margin="dense"
                name="county"
                label="County"
                fullWidth
                value={addUserData.county || ''}
                onChange={handleAddInputChange}
                required={addUserData.role === UserRole.COUNTY_USER || addUserData.role === UserRole.SUBCOUNTY_USER || addUserData.role === UserRole.FACILITY_USER}
              >
                <MenuItem value="">
                  <em>Select County</em>
                </MenuItem>
                {counties.map((county: County) => (
                  <MenuItem key={county.name} value={county.name}>
                    {county.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            
            {/* Only show sub-county field for Facility users */}
            {(addUserData.role === UserRole.FACILITY_USER || addUserData.role === UserRole.SUBCOUNTY_USER) && (
              <TextField
                select
                margin="dense"
                name="subCounty"
                label="Sub County"
                fullWidth
                value={addUserData.subCounty || ''}
                onChange={handleAddInputChange}
                disabled={!addUserData.county}
                required={addUserData.role === UserRole.FACILITY_USER || addUserData.role === UserRole.SUBCOUNTY_USER}
              >
                <MenuItem value="">
                  <em>Select Sub-County</em>
                </MenuItem>
                {addUserData.county && subCounties
                  .filter((sc: SubCounty) => sc.countyName === addUserData.county)
                  .map((subCounty: SubCounty) => (
                    <MenuItem key={subCounty.id} value={subCounty.name}>
                      {subCounty.name}
                    </MenuItem>
                  ))}
              </TextField>
            )}
            
            {/* Only show facility field for Facility users */}
            {addUserData.role === UserRole.FACILITY_USER && (
              <TextField
                select
                margin="dense"
                name="facility"
                label="Facility"
                fullWidth
                value={addUserData.facility || ''}
                onChange={handleAddInputChange}
                disabled={!addUserData.subCounty}
                required={addUserData.role === UserRole.FACILITY_USER}
              >
                <MenuItem value="">
                  <em>Select Facility</em>
                </MenuItem>
                {addUserData.subCounty && facilities
                  .filter((f: Facility) => f.subCountyName === addUserData.subCounty)
                  .map((facility: Facility) => (
                    <MenuItem key={facility.id} value={facility.name}>
                      {facility.name}
                    </MenuItem>
                  ))}
              </TextField>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog} disabled={dialogLoading}>Cancel</Button>
          <Button 
            onClick={handleSubmitAddUser} 
            color="primary" 
            variant="contained"
            disabled={dialogLoading || !addUserData.name || !addUserData.email || !addUserData.password ||
              // Validate county is provided for county, subcounty, and facility users
              ((addUserData.role === UserRole.COUNTY_USER || 
                addUserData.role === UserRole.SUBCOUNTY_USER || 
                addUserData.role === UserRole.FACILITY_USER) && !addUserData.county) ||
              // Validate subcounty is provided for subcounty and facility users
              ((addUserData.role === UserRole.SUBCOUNTY_USER || 
                addUserData.role === UserRole.FACILITY_USER) && !addUserData.subCounty) ||
              // Validate facility is provided for facility users
              (addUserData.role === UserRole.FACILITY_USER && !addUserData.facility)
            }
          >
            {dialogLoading ? <CircularProgress size={24} /> : 'Add User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </>
  );
};

export default UserManagement;
